import { FullPageCard, NavigationPage, NumberInput } from '@/app/components'
import { Button, Icon, LoadingAnimation, Text, Toggle } from '@/components'
import useViewModel from './useViewModel'
import { LoadingContainer } from '@/components/Animations/FadeContainer/FadeContainer'
import { AnimatePresence, motion } from 'motion/react'
import { getCostCategoryDisplayElements } from './utils'
import InactiveTimesSection from './components/InactiveTimesSection'
import SaveMoneyInactiveModal from './components/SaveMoneyInactiveModal'
import SaveMoneyAlert from './components/SaveMoneyAlert'
import CostCategoryModeCard from './components/CostCategoryModeCard'
import CostCategorySavingsCard from './components/CostCategorySavingsCard'
import { BarGraph } from '@/components/BarGraph/V2/BarGraph'

export default function ThermostatCostCategories() {
  const viewModel = useViewModel()
  return (
    <FullPageCard>
      <NavigationPage id="cost-categories" className="p-0">
        <LoadingContainer
          isLoading={viewModel.isLoading}
          loadingContent={
            <div className="flex items-center justify-center w-full h-full">
              <LoadingAnimation type="falling" />
            </div>
          }
          loadedContent={
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-2">
                <div className="flex flex-row items-center justify-between">
                  <Text variant="h3" className="flex">
                    Save Money
                    <a
                      href="https://optiwatt.zendesk.com/hc/en-us/articles/39309598427419-How-do-I-manage-my-thermostat-using-Save-Money"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Learn more about Save Money"
                      className="inline-block ml-2 mt-[1px] self-center"
                    >
                      <Icon
                        name="Info"
                        size={16}
                        className="text-themed-grey-400 cursor-pointer"
                      />
                    </a>
                  </Text>
                  {viewModel.thermostatSettings
                    ?.cost_category_optimization_enabled !== null && (
                    <div
                      onClick={() => {
                        // If trying to enable while there are issues, show unified modal
                        if (
                          !viewModel.saveMoneyEnabled &&
                          (viewModel.isInactive ||
                            viewModel.allCostCategoriesSetToZero ||
                            viewModel.isActiveDREvent ||
                            viewModel.isProgramOverride)
                        ) {
                          viewModel.openSaveMoneyIssueModal()
                        }
                      }}
                    >
                      <Toggle
                        id="save-money-toggle"
                        checked={viewModel.saveMoneyEnabled}
                        onChange={(checked) => {
                          if (
                            checked &&
                            (viewModel.isInactive ||
                              viewModel.allCostCategoriesSetToZero ||
                              viewModel.isActiveDREvent ||
                              viewModel.isProgramOverride)
                          ) {
                            // Intercept enabling when there are issues
                            viewModel.openSaveMoneyIssueModal()
                            return
                          }
                          viewModel.onToggleChange(checked)
                        }}
                        disabled={viewModel.isProgramOverride === true}
                      />
                    </div>
                  )}
                </div>
                <Text variant="body2" className="max-w-[90%]">
                  We'll check rates hourly and shift your temperature based on
                  your preferences
                </Text>
              </div>

              <CostCategorySavingsCard
                label={
                  viewModel.saveMoneyEnabled
                    ? 'Past Month Savings'
                    : 'Est. Annual Savings'
                }
                savingsAmountCents={
                  viewModel.saveMoneyEnabled
                    ? viewModel.monthCostOfOptiwattAdjustmentsCents
                    : viewModel.annualSavingsCents
                }
                loading={viewModel.savingsDataLoading}
                showInfoButton={!viewModel.saveMoneyEnabled}
                hideIfNoSavings={viewModel.saveMoneyEnabled}
              />
              <CostCategoryModeCard
                mode={viewModel.thermostatStatus.mode || 'adjustment'}
                temperature={viewModel.thermostatStatus.temperature}
                title={viewModel.thermostatStatus.title}
                badgeText={viewModel.thermostatStatus.badgeText}
              />
              {viewModel.intervals.length === 0 ? null : (
                <section className="flex flex-col p-4 border border-solid rounded-lg border-base-grey-200">
                  <div className="flex flex-col gap-1">
                    <Text variant="h3">24 Hour Cost Forecast</Text>
                    <Text variant="body2">Updated every minute</Text>
                  </div>
                  <BarGraph {...viewModel.costCategoryBarGraph} />
                </section>
              )}
              <section className="flex flex-col gap-4">
                <AnimatePresence mode="wait">
                  {viewModel.saveMoneyEnabled ||
                  viewModel.allCostCategoriesSetToZero ||
                  viewModel.costCategoryOptimizationEnabledMessage ||
                  viewModel.shouldShowInactiveTimesCard ? (
                    <motion.div
                      key={`cost-category-controls`}
                      initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                      transition={{
                        duration: 0.3,
                        ease: 'easeOut',
                      }}
                      className="flex flex-col w-full gap-8"
                    >
                      {viewModel.costCategoryOptimizationEnabledMessage && (
                        <SaveMoneyAlert
                          costCategoryOptimizationEnabledMessage={
                            viewModel.costCategoryOptimizationEnabledMessage
                          }
                        />
                      )}
                      {viewModel.saveMoneyEnabled ||
                      viewModel.allCostCategoriesSetToZero ? (
                        <>
                          <div>
                            <div className="flex flex-row items-center justify-between">
                              <div className="font-bold">Cost Categories</div>
                              <div className="text-sm">Shift My AC by</div>
                            </div>
                            <div className="flex flex-col gap-6 divide-y divide-solid divide-gray-100">
                              {viewModel.lowCategoryIndex !== 0 &&
                                viewModel.costCategories?.[0] && (
                                  <CostCategoryInput
                                    type="negative"
                                    rootTemp={
                                      viewModel.thermostat
                                        ?.user_desired_cool_setpoint
                                    }
                                    minValue={
                                      viewModel.costCategories[0].minValue
                                    }
                                    maxValue={
                                      viewModel.costCategories[0].maxValue
                                    }
                                    initValue={
                                      viewModel.costCategories[0].initValue
                                    }
                                    onChange={(value) => {
                                      if (
                                        value ===
                                        viewModel.costCategories[0].initValue
                                      ) {
                                        return
                                      }
                                      viewModel.onCostCategoryInputChange(
                                        0,
                                        value,
                                      )
                                    }}
                                  />
                                )}
                              {viewModel.costCategories?.[1] && (
                                <CostCategoryInput
                                  type="low"
                                  rootTemp={
                                    viewModel.thermostat
                                      ?.user_desired_cool_setpoint
                                  }
                                  minValue={
                                    viewModel.costCategories[1].minValue
                                  }
                                  maxValue={
                                    viewModel.costCategories[1].maxValue
                                  }
                                  initValue={
                                    viewModel.costCategories[1].initValue
                                  }
                                  onChange={(value) => {
                                    if (
                                      value ===
                                      viewModel.costCategories[1].initValue
                                    ) {
                                      return
                                    }
                                    viewModel.onCostCategoryInputChange(
                                      1,
                                      value,
                                    )
                                  }}
                                />
                              )}
                              {viewModel.costCategories?.[2] && (
                                <CostCategoryInput
                                  type="medium"
                                  rootTemp={
                                    viewModel.thermostat
                                      ?.user_desired_cool_setpoint
                                  }
                                  minValue={
                                    viewModel.costCategories[2].minValue
                                  }
                                  maxValue={
                                    viewModel.costCategories[2].maxValue
                                  }
                                  initValue={
                                    viewModel.costCategories[2].initValue
                                  }
                                  onChange={(value) => {
                                    if (
                                      value ===
                                      viewModel.costCategories[2].initValue
                                    ) {
                                      return
                                    }
                                    viewModel.onCostCategoryInputChange(
                                      2,
                                      value,
                                    )
                                  }}
                                />
                              )}
                              {viewModel.costCategories?.[3] && (
                                <CostCategoryInput
                                  type="high"
                                  rootTemp={
                                    viewModel.thermostat
                                      ?.user_desired_cool_setpoint
                                  }
                                  minValue={
                                    viewModel.costCategories[3].minValue
                                  }
                                  maxValue={
                                    viewModel.costCategories[3].maxValue
                                  }
                                  initValue={
                                    viewModel.costCategories[3].initValue
                                  }
                                  onChange={(value) => {
                                    if (
                                      value ===
                                      viewModel.costCategories[3].initValue
                                    ) {
                                      return
                                    }
                                    viewModel.onCostCategoryInputChange(
                                      3,
                                      value,
                                    )
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          <Button
                            id="cost-category-navigate"
                            variant="primary-subtle"
                            small
                            onClick={viewModel.onEditCostCategoriesClick}
                          >
                            Edit Cost Categories
                          </Button>
                        </>
                      ) : null}
                      {viewModel.shouldShowInactiveTimesCard ? (
                        <InactiveTimesSection
                          onCardClick={viewModel.onInactiveTimesCardClick}
                          lines={viewModel.inactiveTimesCardSubtitleLines}
                          isModalOpen={false}
                          onCloseModal={() => {}}
                          onEditClick={viewModel.onInactiveTimesCardClick}
                        />
                      ) : null}
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </section>
            </div>
          }
        />
      </NavigationPage>

      <SaveMoneyInactiveModal
        isOpen={
          viewModel.isZeroCategoriesModalOpen ||
          viewModel.isInactiveTimesModalOpen
        }
        onClose={() => {
          viewModel.closeZeroCategoriesModal()
          viewModel.closeInactiveTimesModal()
        }}
        hasInactiveTimesIssue={viewModel.isInactive}
        hasZeroCategoriesIssue={viewModel.allCostCategoriesSetToZero}
        hasDREventIssue={viewModel.isActiveDREvent}
        hasProgramIssue={viewModel.isProgramOverride}
        inactiveTimesLines={viewModel.inactiveTimesCardSubtitleLines}
        onEditInactiveTimes={viewModel.onInactiveTimesCardClick}
        activeDrEvent={viewModel.activeDrEvent}
      />
    </FullPageCard>
  )
}

type CostCategoryInputProps = {
  type: 'negative' | 'low' | 'medium' | 'high'
  minValue: number
  maxValue: number
  initValue: number
  rootTemp?: number
  onChange: (value: number) => void
}
function CostCategoryInput({
  type,
  minValue,
  maxValue,
  initValue,
  rootTemp,
  onChange,
}: CostCategoryInputProps) {
  const { icon, title, description } = getCostCategoryDisplayElements({
    maxValue,
    minValue,
    type,
  })
  const shiftValue = (rootTemp ?? 0) + initValue

  return (
    <div className="flex justify-between gap-4 pt-6">
      <div className="flex flex-row gap-1">
        <div className="flex-none">{icon}</div>
        <div className="flex flex-col">
          <Text variant="body1">{title}</Text>
          {description}
        </div>
      </div>
      <div className="flex flex-col items-center justify-center">
        <NumberInput
          initialValue={initValue}
          range={[-30, 30]}
          onChange={onChange}
          showPlusSign
          suffix="°"
        />
        {rootTemp && (
          <div className="text-xs text-themed-grey-500 mt-2">
            (Will {initValue === 0 ? `remain at ` : `shift to `}
            <span className="font-bold">{shiftValue}°</span>)
          </div>
        )}
      </div>
    </div>
  )
}

// SetInactiveTimesCard moved into components/InactiveTimesSection
